import { DOCUMENT } from "@angular/common";
import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from "@angular/core";
import ResizeObserver from "resize-observer-polyfill";
import { Observable, Subscription } from "rxjs";

import { Asset, AssetSource } from "@hermes/api-model-asset";
import { Product, ProductTemplateType } from "@hermes/api-model-product";
import { WINDOW } from "@hermes/app-core";

import { AssetSourceValue, DOWN, UP } from "@hermes/utils-generic/constants";

const marginBottom = 10;

const arrowIconHeight = 50;

@Component({
    selector: "h-product-image-zoom-gallery",
    templateUrl: "./product-image-zoom-gallery.component.html",
    styleUrls: ["./product-image-zoom-gallery.component.scss"],
})
export class ProductImageZoomGalleryComponent
    implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
    /**
     * Gallery : dynamically calculate the size of the gallery
     */
    @ViewChild("galleryContainer")
    public galleryContainer!: ElementRef;

    /** Gallery container : dynamically calculate the size of the gallery */
    @ViewChild("zoomGalleryContainer")
    public zoomGalleryContainer!: ElementRef;

    /**
     * Thumbnail element: dynamically calculate the size of the gallery
     */
    @ViewChild("thumbnail")
    public thumbnail!: ElementRef;

    /**
     * list of asset of current product
     */
    @Input()
    public assets!: Asset[];

    /**
     * current product
     */
    @Input()
    public product!: Product;

    /**
     * current index of slelected image
     */
    @Input()
    public currentIndex: number = 0;

    /**
     * true if we're on mobile resolutions.
     */
    @Input()
    public isMobile: boolean = true;

    /**
     * when index change send event to parent component
     */
    @Output()
    public indexChange: EventEmitter<number> = new EventEmitter<number>();

    /**
     * Prefix id of tab
     */
    @Input()
    public prefixTab: string = "tab";

    /**
     * Orientation of the gallery
     */
    @Input()
    public orientation: "vertical" | "horizontal" = "vertical";

    @Input()
    public infiniteLoop: boolean = false;

    public listDirections!: string[];

    public thumbnailSize: number = 130;

    public lastIndex: number = 0;

    /**
     * Current translateY value
     */
    public currentTranslateY = 0;

    /**
     * The maximum number of images visible in the viewport, it changes depending on the screen size
     */
    public maxDisplayedImagesInViewPort: number = 0;

    /**
     * Used to calculate the value of translateY when we click on arrow top
     */
    public numberImagesOutOfViewPortFromTop: number = 0;

    /**
     * Used to calculate the value of translateY when we click on arrow bottom
     */
    public numberImagesOutOfViewPortFromBottom: number = 0;

    /**
     * Used to calculate the value of numberImagesOutOfViewPortFromTop and numberImagesOutOfViewPortFromBottom when we click on arrow bottom when resizing screen
     */
    public numberImagesOutOfViewPort: number = 0;

    /**
     * import ProductTemplateType
     */
    public productTemplateType = ProductTemplateType;

    /**
     * Used to make a difference between platform assets and scene7 assets
     */
    public assetPlatforme: AssetSource = AssetSourceValue.Platforme;

    /**
     * To save all the subscription to be able to remove them when you close the zoom modal
     */
    private subscription: Subscription = new Subscription();

    constructor(
        @Inject(DOCUMENT) private document: Document,
        @Inject(WINDOW) private window: Window,
    ) {}

    public ngOnInit(): void {
        this.lastIndex = this.assets.length - 1;
        this.initDirectionList();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.assets && changes.assets.currentValue) {
            this.lastIndex = this.assets.length - 1;
        }
    }

    public ngAfterViewInit(): void {
        if (ResizeObserver && this.isOrientationVertical()) {
            this.subscription.add(
                this.createResizeObservable(
                    this.zoomGalleryContainer.nativeElement,
                ).subscribe(() => {
                    this.handleMaxDisplayedImages();
                }),
            );
        }
    }

    /**
     * When click to thumbnail
     *
     * @param index : current index
     */
    public handleThumbnailSelectedWithClick(index: number): void {
        this.indexChange.emit(index);
    }

    public initDirectionList(): void {
        this.listDirections = this.isOrientationVertical()
            ? ["up", "down"]
            : ["left", "right"];
    }
    /**
     * Manage navigation between images using the keyboard
     *
     * @param event  keyboard event
     * @param index  current image index
     */
    public handleThumbnailSelectedWithKey(
        event: KeyboardEvent,
        index: number,
    ): void {
        switch (event.key) {
            case DOWN: {
                this.changeSelectionDown(index);
                break;
            }
            case UP: {
                this.changeSelectionOnUp(index);
                break;
            }
            default:
        }
    }

    public changeSelectionOnUp(index: number): void {
        if (index === 0) {
            if (this.infiniteLoop) {
                this.focusToTab(this.lastIndex);
                this.indexChange.emit(this.lastIndex);
            }
            return;
        }

        if (this.isFocusOutOfViewPort(index) && this.isOrientationVertical()) {
            this.scrollTop();
        }

        this.focusToTab(index - 1);
        this.indexChange.emit(index - 1);
    }

    public changeSelectionDown(index: number): void {
        if (index === this.lastIndex) {
            if (this.infiniteLoop) {
                this.focusToTab(0);
                this.indexChange.emit(0);
            }
            return;
        }

        if (
            this.isFocusOutOfViewPort(index + 1) &&
            this.isOrientationVertical()
        ) {
            this.scrollBottom();
        }

        this.focusToTab(index + 1);
        this.indexChange.emit(index + 1);
    }

    /**
     * Set focus to tab
     *
     * @param index current image index
     */
    public focusToTab(index: number): void {
        const element = this.document.getElementById(this.prefixTab + index);
        element?.focus({
            preventScroll: this.isOrientationVertical(),
        });
        if (index === 0) {
            this.window.scrollTo(0, 0);
        }
    }

    /**
     * Generate aria-controls dynamicly with index
     *
     * @param index current image index
     */
    public prefixerAriaControls(index: number): string {
        return `tabpanel${index}`;
    }

    public trackByUrl(index: number, item: Asset): string {
        return `${index}_${item.url}`;
    }

    /**
     * After each resize of the screen we need to calculate the height of the different elements
     */
    public handleMaxDisplayedImages(): void {
        const galleryHeight = this.window.innerHeight - arrowIconHeight * 2;
        this.maxDisplayedImagesInViewPort =
            galleryHeight /
            (this.thumbnail.nativeElement.offsetHeight + marginBottom);

        this.updateNumberImagesOutOfViewPort();
    }

    /**
     * Calculate numbers of thumbnails outside viewport
     */
    public updateNumberImagesOutOfViewPort(): void {
        if (this.maxDisplayedImagesInViewPort >= this.assets.length) {
            this.currentTranslateY = 0;
        }

        this.numberImagesOutOfViewPort =
            this.assets.length - this.maxDisplayedImagesInViewPort;

        this.infiniteLoop = this.numberImagesOutOfViewPort <= 0;
        if (this.currentTranslateY === 0) {
            this.numberImagesOutOfViewPortFromTop = 0;
            this.numberImagesOutOfViewPortFromBottom =
                this.numberImagesOutOfViewPort;
        } else {
            this.numberImagesOutOfViewPortFromTop =
                this.numberImagesOutOfViewPort / 2;
            this.currentTranslateY =
                -this.numberImagesOutOfViewPortFromTop *
                (this.thumbnail.nativeElement.offsetHeight + marginBottom);
            this.numberImagesOutOfViewPortFromBottom =
                this.numberImagesOutOfViewPort -
                this.numberImagesOutOfViewPortFromTop;
        }
    }

    /**
     * when user click in bottom arrow icon
     */
    public scrollTop(): void {
        const scrollStep: number =
            this.numberImagesOutOfViewPortFromTop >=
            this.maxDisplayedImagesInViewPort
                ? this.maxDisplayedImagesInViewPort
                : this.numberImagesOutOfViewPortFromTop;
        this.currentTranslateY +=
            scrollStep *
            (this.thumbnail.nativeElement.offsetHeight + marginBottom);

        this.numberImagesOutOfViewPortFromTop -= scrollStep;
        this.numberImagesOutOfViewPortFromBottom += scrollStep;
    }

    /**
     * When user click in bottom arrow icon
     */
    public scrollBottom(): void {
        const scrollStep: number =
            this.numberImagesOutOfViewPortFromBottom >=
            this.maxDisplayedImagesInViewPort
                ? this.maxDisplayedImagesInViewPort
                : this.numberImagesOutOfViewPortFromBottom;

        this.currentTranslateY -=
            scrollStep *
            (this.thumbnail.nativeElement.offsetHeight + marginBottom);

        this.numberImagesOutOfViewPortFromBottom -= scrollStep;
        this.numberImagesOutOfViewPortFromTop += scrollStep;
    }

    /**
     * For A11y, use case when navigating with the keyboard (top / bottom), we must ensure that the focus is always visible
     *
     * @param index : currentIndex
     */
    public isFocusOutOfViewPort(index: number): boolean {
        return (
            index % Math.round(this.maxDisplayedImagesInViewPort) === 0 &&
            index >= Math.round(this.maxDisplayedImagesInViewPort)
        );
    }

    /**
     * Creates an height observable from a ResizeObserver.
     *
     * @param element the dom element to observe
     */
    public createResizeObservable(element: HTMLElement): Observable<number> {
        return new Observable((observer) => {
            const heightObserver: ResizeObserver = new ResizeObserver(
                (entries) => {
                    for (const entry of entries) {
                        observer.next(entry.contentRect.height);
                    }
                },
            );
            heightObserver.observe(element);
            return () => {
                heightObserver.disconnect();
            };
        });
    }

    /**
     * Format translateY
     */
    public getCurrentTranslateYString(): string {
        return `translateY(${this.currentTranslateY}px)`;
    }

    /**
     * true : if orientation is vertical
     */
    public isOrientationVertical(): boolean {
        return this.orientation === "vertical";
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }
}
